<template>
  <div class="select-agent-container d-flex flex-column w-100 default-text-font"
    :style="{ minHeight: '420px', minWidth: '850px', maxHeight: maxHeight }">
    <!-- リストの上の説明文 -->
    <div class="mt-3 mb-3 flex-shrink-0">
      <slot name="description">
        <div v-if="description" style="height: 20px;">
          {{ description }}
        </div>
      </slot>
    </div>

    <!-- 部署ツリーと端末一覧の2カラムエリア -->
    <div class="d-flex flex-row flex-grow-1 min-height-0" style="height: 500px;">

      <!-- 左側：部署ツリー -->
      <div class="d-flex flex-column flex-shrink-0" style="min-width: 280px; max-width: 280px;">
        <div class="flex-shrink-0">
          <div class="title-belt-table">
            <span>部署</span>
            <div class="btn-group btn-group-toggle ms-auto" data-bs-toggle="buttons" style="margin-bottom: 8px;">
              <label class="btn btn-scv-bgcolor" :class="{ active: btnContainsAll }"
                style="padding-top: 0px; padding-bottom: 0px; display: flex; align-items: center;">
                <input class="btn-check" type="checkbox" v-model="btnContainsAll" @change="onBtnContainsAll"
                  autocomplete="off">
                配下全表示
              </label>
            </div>
          </div>
        </div>
        <div class="flex-grow-1 overflow-auto">
          <DivisionTreeSelect ref="divSelectRef" :divSettings="divSettings" :selectDivision="selectDivision"
            :arViewableDivisionID="viewableDivisionIds" :isInheritViewable="isInheritViewable"
            :defaultDivisionID="defaultDivisionId" @selectItem="onSelectDivision" style="height: 100%;" />
        </div>
      </div>

      <div style="min-width: 16px;"></div>

      <!-- 右側：端末一覧 -->
      <div class="d-flex flex-column flex-grow-1 min-height-0">
        <ListTitleBar ref="listTitleBarRef" titleCaption="端末一覧" :enableSearchText="true" :rowCount="rowCount"
          :isUseMenuItemList="true" :selectCount="selectCount" unit="台" :isShowOperationButton="false" :rowCountArea="true"
          class="flex-shrink-0" @onSearchBoxKeydown="onSearch" @onBtnClear="onClearSearch" />
        <ListCtrl ref="listCtrlAgentRef" class="flex-grow-1 min-height-0" :columns="columns" :selectType="3"
          :getCellTextFunc="getCellText" :sortDeleteIndex="false" :selectRowCheckButtonFunc="selectRowCheckButtonFunc"
          :selectHeaderCheckButtonFunc="selectHeaderCheckButtonFunc" @onSelectIndexChanged="onSelectedIDs"
          style="height: 0 !important; height: 100%;" />
      </div>

    </div>
    <slot name="body-bottom-extension" />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, nextTick } from 'vue';
import { DivisionData, DivisionDBData } from "@/models/DivisionData";
import { useSelectedDivisionStore } from '@/models/Store/DivisionStore';
import { useUserSettingStore } from '@/models/Store/UserSettingStore';
import { SearchColumnData } from '@/models/SearchColumnData';
import DivisionTreeSelect from '@/components/DivisionTree/DivisionTreeSelect.vue';
import { ListCtrl } from 'lib-vue-v2/components';
import ListTitleBar from '@/components/ListTitleBar.vue';

// --- Props型定義 ---
interface Props {
  /** 部署連動用の最小限の端末IDと所属部署IDのペア配列 */
  agentIds: Array<{ agentID: number; divisionID: number }>;
  /** 部署ツリーデータ */
  divisionList: Array<DivisionDBData>;
  /** 列定義 */
  columns: Array<SearchColumnData>;
  /** セルテキスト取得関数 */
  getCellTextFunc: (columnId: number, agentId: number) => string;
  /** 選択状態のSet */
  selectedAgentIds: Set<number>;
  /** 説明文 */
  description?: string;
  /** 閲覧可能部署Id */
  arViewableDivisionID?: Array<number> | null;
  /** 閲覧設定を子要素に引き継ぐか */
  isInheritViewable?: boolean;
  /** 閲覧不可の部署を指定された際に初期選択する部署ID */
  defaultDivisionID?: number;
  /** チェックボックス押下時（変更前） */
  selectRowCheckButtonFunc?: (...args: any[]) => unknown;
  /** ヘッダのチェックボックス押下時（変更前） */
  selectHeaderCheckButtonFunc?: (...args: any[]) => unknown;
  /** 選択した部署を記憶しない場合はfalseを設定(デフォルトtrue) */
  isRememberSelection?: boolean;
  /** コンポーネント全体の高さ上限（スクロールを発生させる高さの上限閾値） */
  maxHeight?: string;
}

// withDefaultsを使用したPropsの定義とデフォルト値の設定
const props = withDefaults(defineProps<Props>(), {
  description: '',
  arViewableDivisionID: null,
  isInheritViewable: true,
  defaultDivisionID: -1,
  selectRowCheckButtonFunc: undefined,
  selectHeaderCheckButtonFunc: undefined,
  isRememberSelection: true,
  maxHeight: '520px' // デフォルトの上限高さを設定（必要に応じて呼び出し元から '600px' や 'none' などに変更可能）
});

const emit = defineEmits<{
  // 選択行変更時の通知
  (e: 'onChangeSelectAgent', selectedIds: Set<number>): void;
}>();

const userSettingStore = useUserSettingStore();
const selectedDivisionStore = useSelectedDivisionStore();

const divSelectRef = ref<any>(null);
const listCtrlAgentRef = ref<any>(null);
const listTitleBarRef = ref<any>(null);

// --- State ---
const displayAgentIds = ref<number[]>([]);
const divSettings = ref<DivisionData>(new DivisionData());
const selectDivision = ref<DivisionData>(new DivisionData());
const btnContainsAll = ref(true);
const rowCount = ref(0);

// --- Computed ---
const viewableDivisionIds = computed(() => {
  if (!props.arViewableDivisionID) return userSettingStore.getViewableDivisionIds;
  return props.arViewableDivisionID; // cloneDeepを廃止して高速化
});

const defaultDivisionId = computed(() => {
  if (props.defaultDivisionID === -1) return userSettingStore.getDivisionId;
  return props.defaultDivisionID;
});

const selectCount = computed(() => {
    return props.selectedAgentIds.size;
});

// ==========================================
// Methods
// ==========================================
const updateRowCount = async () => {
  await nextTick();
  if (listCtrlAgentRef.value) {
    rowCount.value = listCtrlAgentRef.value.getDisplayListIndex().length;
  }
};

const initializeData = async () => {
  if (listCtrlAgentRef.value) listCtrlAgentRef.value.setSort(0, true);
  if (listTitleBarRef.value) listTitleBarRef.value.initListTitleBar();
  if (props.divisionList) {
    divSettings.value.Load(props.divisionList);
    const selectedDivision = await selectedDivisionStore.getDivisionInfo();
    btnContainsAll.value = selectedDivision.enableChildDivision;
    const searchDivision = divSettings.value.GetDivisionData(selectedDivision.divisionId);
    if (searchDivision) {
      selectDivision.value = searchDivision;
    } else {
      selectDivision.value = divSettings.value;
    }
  }
  updateTableData();
};

const onSelectDivision = (newSelectDivision: DivisionData) => {
  selectDivision.value = newSelectDivision;
  if (props.isRememberSelection) {
    selectedDivisionStore.setDivisionInfo(newSelectDivision.divisionId, newSelectDivision.divisionName, btnContainsAll.value);
  }
  updateTableData();
};

const updateTableData = () => {
  const divisionIDs: number[] = [];
  if (btnContainsAll.value) {
    selectDivision.value.GetAllDivisionID(divisionIDs);
  } else {
    divisionIDs.push(selectDivision.value.divisionId);
  }

  // props.agentIds から対象部署の agentID のみを抽出してセット
  displayAgentIds.value = props.agentIds
    .filter(x => divisionIDs.includes(x.divisionID))
    .map(x => Number(x.agentID));

  if (listCtrlAgentRef.value) {
    listCtrlAgentRef.value.updateList(displayAgentIds.value.length);
  }
  updateCheckStatus();
  updateRowCount();
};

const updateCheckStatus = () => {
  const selectIndex: number[] = [];
  displayAgentIds.value.forEach((agentId, index) => {
    if (props.selectedAgentIds.has(agentId)) {
      selectIndex.push(index);
    }
  });
  if (listCtrlAgentRef.value) {
    listCtrlAgentRef.value.selectRows(selectIndex);
  }
};

const getCellText = (columnId: number, index: number): string => {
  const agentId = displayAgentIds.value[index];
  if (agentId === undefined) return "";
  return props.getCellTextFunc(columnId, agentId);
};

const onSelectedIDs = (selectedIndices: number[]) => {
  const newSelectedSet = new Set(props.selectedAgentIds);
  const visibleAgentIds = displayAgentIds.value;
  const currentlyCheckedInView = new Set(selectedIndices.map(idx => visibleAgentIds[idx]));
  visibleAgentIds.forEach(agentId => {
    if (currentlyCheckedInView.has(agentId)) {
      newSelectedSet.add(agentId);
    } else {
      newSelectedSet.delete(agentId);
    }
  });
  emit("onChangeSelectAgent", newSelectedSet);
};

const onSearch = (searchedText: string) => {
  if (listCtrlAgentRef.value) listCtrlAgentRef.value.search(searchedText);
  updateCheckStatus();
  updateRowCount();
};

const onClearSearch = () => {
  if (listCtrlAgentRef.value) listCtrlAgentRef.value.search("");
  updateCheckStatus();
  updateRowCount();
};

const onBtnContainsAll = () => {
  selectedDivisionStore.setDivisionInfo(selectDivision.value.divisionId, selectDivision.value.divisionName, btnContainsAll.value);
  updateTableData();
};

watch([() => props.divisionList, () => props.agentIds], () => {
  initializeData();
});

onMounted(() => {
  initializeData();
});

defineExpose({
  initializeData,
  selectDivision,
  btnContainsAll
});
</script>

<style lang="scss" scoped>
// 各子要素の高さ計算や潰れを防止するユーティリティクラス
.min-height-0 {
  min-height: 0 !important;
}
</style>
